/**
 * Servizio di audit log — traccia ogni azione sul portale.
 * Scrive nella collection Firestore `audit_log`.
 */

import { collection, addDoc, serverTimestamp, query, orderBy, limit, getDocs, where, Timestamp, startAfter } from 'firebase/firestore';
import { db } from '@/lib/firebase';

export const AUDIT_ACTIONS = {
  PRACTICE_CREATED: 'practice_created',
  PRACTICE_UPDATED: 'practice_updated',
  PRACTICE_DELETED: 'practice_deleted',
  PRACTICE_TRANSITION: 'practice_transition',
  DOC_CARICATI: 'doc_caricati',
  EXCEL_UPLOADED: 'excel_uploaded',
  USER_LOGIN: 'user_login',
  USER_LOGOUT: 'user_logout',
  FASTWEB_TAKEOVER: 'fastweb_takeover',
  // Scritte SOLO dalla Cloud Function dailySlaAlerts (Admin SDK): rendono
  // verificabili nello Storico Operazioni i solleciti automatici via email.
  SLA_ALERT_SENT: 'sla_alert_inviato',
  SLA_ALERT_FAILED: 'sla_alert_fallito',
};

/** Label italiane per ogni azione di audit. */
export const AUDIT_ACTION_LABELS = {
  [AUDIT_ACTIONS.PRACTICE_CREATED]: 'Pratica creata',
  [AUDIT_ACTIONS.PRACTICE_UPDATED]: 'Pratica modificata',
  [AUDIT_ACTIONS.PRACTICE_DELETED]: 'Pratica eliminata',
  [AUDIT_ACTIONS.PRACTICE_TRANSITION]: 'Transizione stato',
  [AUDIT_ACTIONS.DOC_CARICATI]: 'Doc caricati',
  [AUDIT_ACTIONS.EXCEL_UPLOADED]: 'Upload Excel',
  [AUDIT_ACTIONS.USER_LOGIN]: 'Accesso utente',
  [AUDIT_ACTIONS.USER_LOGOUT]: 'Uscita utente',
  [AUDIT_ACTIONS.FASTWEB_TAKEOVER]: 'Presa in carico Fastweb',
  [AUDIT_ACTIONS.SLA_ALERT_SENT]: 'Sollecito inviato',
  [AUDIT_ACTIONS.SLA_ALERT_FAILED]: 'Sollecito non recapitato',
};

/** Colori semantici per ogni azione (badge). */
export const AUDIT_ACTION_COLORS = {
  [AUDIT_ACTIONS.PRACTICE_CREATED]: { bg: 'bg-blue-50', text: 'text-fw-blue', border: 'border-fw-blue/20' },
  [AUDIT_ACTIONS.PRACTICE_UPDATED]: { bg: 'bg-fw-light', text: 'text-fw-muted', border: 'border-fw-border' },
  [AUDIT_ACTIONS.PRACTICE_DELETED]: { bg: 'bg-red-50', text: 'text-fw-red', border: 'border-fw-red/20' },
  [AUDIT_ACTIONS.PRACTICE_TRANSITION]: { bg: 'bg-yellow-50', text: 'text-fw-dark', border: 'border-fw-yellow/30' },
  [AUDIT_ACTIONS.DOC_CARICATI]: { bg: 'bg-yellow-50', text: 'text-fw-dark', border: 'border-fw-yellow/30' },
  [AUDIT_ACTIONS.EXCEL_UPLOADED]: { bg: 'bg-fw-purple-subtle', text: 'text-fw-purple', border: 'border-fw-purple/20' },
  [AUDIT_ACTIONS.USER_LOGIN]: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-fw-green/20' },
  [AUDIT_ACTIONS.USER_LOGOUT]: { bg: 'bg-fw-light', text: 'text-fw-muted', border: 'border-fw-border' },
  [AUDIT_ACTIONS.FASTWEB_TAKEOVER]: { bg: 'bg-yellow-50', text: 'text-fw-dark', border: 'border-fw-yellow/30' },
  [AUDIT_ACTIONS.SLA_ALERT_SENT]: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-fw-yellow/30' },
  [AUDIT_ACTIONS.SLA_ALERT_FAILED]: { bg: 'bg-red-50', text: 'text-fw-red', border: 'border-fw-red/20' },
};

/**
 * Ritorna il testo del badge leggibile per un record di audit (usato
 * nello Storico Operazioni). Distingue tra azioni generiche e le transizioni
 * di workflow, dove il testo dipende dallo stato di destinazione e
 * dall'attore (details.actorRole).
 *
 * Per i documenti (in_corso → approvazione_hq / rifiutato):
 *  - actorRole === 'fastweb'  → "Documenti approvati/rifiutati da Fastweb (in vece di HQ)"
 *  - actorRole === 'hq' o assente → "Documenti approvati/rifiutati da HQ"
 *    (l'assenza di actorRole indica un log legacy antecedente all'introduzione
 *    del controllore Fastweb: retrocompat = "da HQ").
 */
export function readableAuditAction(log) {
  const { action, details } = log;
  if (action === AUDIT_ACTIONS.PRACTICE_CREATED) return 'Nuova pratica creata';
  if (action === AUDIT_ACTIONS.PRACTICE_UPDATED) return 'Pratica modificata';
  if (action === AUDIT_ACTIONS.PRACTICE_DELETED) return 'Pratica eliminata';
  if (action === AUDIT_ACTIONS.DOC_CARICATI) return 'Documenti caricati';
  if (action === AUDIT_ACTIONS.EXCEL_UPLOADED) {
    const name = details?.fileName ? ` "${details.fileName}"` : '';
    return `Upload Excel${name}`;
  }
  if (action === AUDIT_ACTIONS.USER_LOGIN) return 'Accesso effettuato';
  if (action === AUDIT_ACTIONS.USER_LOGOUT) return 'Uscita effettuata';
  if (action === AUDIT_ACTIONS.FASTWEB_TAKEOVER) return 'Pratica presa in carico da Fastweb';
  if (action === AUDIT_ACTIONS.SLA_ALERT_SENT) {
    const dest = details?.tipo === 'impresa' ? "all'impresa" : 'al professionista';
    const gg = details?.giorni != null ? ` — pratica ferma da ${details.giorni} gg lav.` : '';
    return `Sollecito automatico inviato ${dest}${gg}`;
  }
  if (action === AUDIT_ACTIONS.SLA_ALERT_FAILED) {
    const dest = details?.tipo === 'impresa' ? "all'impresa" : 'al professionista';
    return `Sollecito automatico ${dest} NON recapitato (email fallita)`;
  }
  if (action === AUDIT_ACTIONS.PRACTICE_TRANSITION && details) {
    // Loop fornitore↔ditta: rifiutato_ditta → approvazione_hq = "Preventivo aggiornato dal fornitore"
    if (details.from === 'rifiutato_ditta' && details.to === 'approvazione_hq') return 'Preventivo aggiornato dal professionista';
    // Approvazione documenti: HQ default, Fastweb per pratiche HQ-internal.
    if (details.to === 'approvazione_hq') {
      return details.actorRole === 'fastweb'
        ? 'Documenti approvati da Fastweb (in vece di HQ)'
        : 'Documenti approvati da HQ';
    }
    // Ditta approva preventivo: approvazione_hq → chiuso (vs vecchio HQ chiude direttamente)
    if (details.to === 'chiuso' && details.from === 'approvazione_hq') return 'Preventivo approvato dalla ditta';
    if (details.to === 'chiuso') return 'Pratica chiusa';
    // Rifiuto Ditta sul preventivo
    if (details.to === 'rifiutato_ditta') return 'Preventivo rifiutato dalla ditta';
    // Rifiuto documenti: HQ default, Fastweb per pratiche HQ-internal.
    if (details.to === 'rifiutato') {
      return details.actorRole === 'fastweb'
        ? 'Documenti rifiutati da Fastweb (in vece di HQ)'
        : 'Documenti rifiutati da HQ';
    }
    if (details.to === 'in_corso') return 'Pratica avviata';
    if (details.to === 'non_avviato') return 'Pratica riaperta';
  }
  return AUDIT_ACTION_LABELS[action] || action;
}

/**
 * Restituisce una descrizione aggiuntiva per un record di audit,
 * solo quando aggiunge informazione rispetto al badge azione.
 * Ritorna null se la descrizione sarebbe ridondante col badge.
 */
export function describeAuditAction(log) {
  const details = log.details;

  if (log.action === AUDIT_ACTIONS.PRACTICE_TRANSITION && details) {
    const stateLabels = {
      non_avviato: 'Non avviato',
      in_corso: 'In corso',
      approvazione_hq: 'Documenti approvati',
      chiuso: 'Chiuso',
      rifiutato: 'Rifiutato',
      rifiutato_ditta: 'Rifiutato dalla ditta',
    };
    const from = stateLabels[details.from] || details.from;
    const to = stateLabels[details.to] || details.to;
    let desc = `${from} → ${to}`;
    if (details.noteRifiuto) desc += ` — "${details.noteRifiuto}"`;
    if (details.noteRifiutoDitta) desc += ` — "${details.noteRifiutoDitta}"`;
    return desc;
  }

  if (log.action === AUDIT_ACTIONS.EXCEL_UPLOADED && details) {
    return `"${details.fileName}" — ${details.imported} importate, ${details.skipped} saltate`;
  }

  // Per tutte le altre azioni il badge è già sufficiente
  return null;
}

/**
 * Scrive un record di audit log.
 * @param {Object} params
 * @param {string} params.userId - UID dell'utente
 * @param {string} params.userEmail - Email dell'utente
 * @param {string} params.action - Tipo di azione (da AUDIT_ACTIONS)
 * @param {string} [params.practiceId] - ID della pratica coinvolta
 * @param {Object} [params.details] - Dettagli aggiuntivi (campo modificato, vecchio/nuovo valore, ecc.)
 */
export async function writeAuditLog({ userId, userEmail, action, practiceId = null, details = null }) {
  if (!db) return;
  try {
    await addDoc(collection(db, 'audit_log'), {
      userId,
      userEmail,
      action,
      practiceId,
      details,
      timestamp: serverTimestamp(),
    });
  } catch (e) {
    console.error('Audit log write error:', e);
  }
}

/**
 * Recupera gli ultimi N record di audit log.
 * @param {number} maxResults - Numero massimo di risultati (default 100)
 * @param {string} [practiceId] - Filtra per pratica specifica
 */
export async function getAuditLogs(maxResults = 100, practiceId = null) {
  if (!db) return [];
  try {
    let q;
    if (practiceId) {
      q = query(
        collection(db, 'audit_log'),
        where('practiceId', '==', practiceId),
        orderBy('timestamp', 'desc'),
        limit(maxResults)
      );
    } else {
      q = query(
        collection(db, 'audit_log'),
        orderBy('timestamp', 'desc'),
        limit(maxResults)
      );
    }
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ _docId: d.id, ...d.data() }));
  } catch (e) {
    console.error('Audit log read error:', e);
    return [];
  }
}

/**
 * Recupera record di audit log con filtri incrementali (AND).
 * I filtri action e userEmail sono applicati lato client perché Firestore
 * non supporta più di un filtro di uguaglianza su campi diversi senza indici compositi.
 * Il filtro temporale (dateFrom/dateTo) è applicato nella query Firestore.
 *
 * @param {Object} filters
 * @param {string} [filters.dateFrom] - Data inizio (YYYY-MM-DD), inclusiva
 * @param {string} [filters.dateTo] - Data fine (YYYY-MM-DD), inclusiva
 * @param {string} [filters.action] - Tipo azione (da AUDIT_ACTIONS)
 * @param {string} [filters.userEmail] - Email utente
 * @param {string} [filters.practiceId] - ID pratica
 * @param {string} [filters.studioProgettazione] - Studio Progettazione (richiede practices per il mapping)
 * @param {number} [filters.maxResults] - Limite risultati (default 500)
 */
/**
 * Recupera record di audit log con filtri e supporto paginazione con cursore.
 * Ritorna { results, lastDoc, hasMore }.
 */
export async function getFilteredAuditLogs(filters = {}) {
  if (!db) return { results: [], lastDoc: null, hasMore: false };
  const { dateFrom, dateTo, action, userEmail, practiceId, maxResults = 500, cursor = null } = filters;

  try {
    const constraints = [orderBy('timestamp', 'desc')];

    if (dateFrom) {
      const from = new Date(dateFrom);
      from.setHours(0, 0, 0, 0);
      constraints.push(where('timestamp', '>=', Timestamp.fromDate(from)));
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      constraints.push(where('timestamp', '<=', Timestamp.fromDate(to)));
    }
    if (practiceId) {
      constraints.push(where('practiceId', '==', practiceId));
    }

    if (cursor) {
      constraints.push(startAfter(cursor));
    }

    constraints.push(limit(maxResults));

    const q = query(collection(db, 'audit_log'), ...constraints);
    const snap = await getDocs(q);
    const lastDoc = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
    let results = snap.docs.map((d) => ({ _docId: d.id, ...d.data() }));

    // Filtri lato client (evita indici compositi Firestore)
    if (action) {
      results = results.filter((r) => r.action === action);
    }
    if (userEmail) {
      results = results.filter((r) => r.userEmail === userEmail);
    }

    return { results, lastDoc, hasMore: snap.docs.length >= maxResults };
  } catch (e) {
    console.error('Audit log filtered read error:', e);
    return { results: [], lastDoc: null, hasMore: false };
  }
}
